import { isPresetWidgetType } from '../presets/registry';
import { DEFAULT_WIDGET_APPEARANCE } from '../utils/widgetThemes';
import { emptyKpiCard } from '../utils/kpiItems';
import { REPORT_DATA_SOURCE } from '../utils/reportQueries';

/**
 * Unified Reports widget-type registry.
 *
 * Future single source of truth for type metadata. Conceptual shape:
 *   type → { meta, createDefault, Editor, View, skipDataFetch }
 *
 * Editor / View are bound via `editorKind` / `viewKind` (not component imports)
 * so this module stays free of circular imports and does not pull viewer
 * implementations into the designer catalog or config validation.
 *
 * Adding a type: register one entry here (+ a View implementation if it is new).
 */

export const VIEW_KIND = {
  chart: 'chart',
  table: 'table',
  stat: 'stat',
  gauge: 'gauge',
  progress: 'progress',
  preset: 'preset',
};

export const EDITOR_KIND = {
  chart: 'chart',
  pie: 'pie',
  table: 'table',
  stat: 'stat',
  gauge: 'gauge',
  preset: 'preset',
};

const UNKNOWN_META = (type) => ({
  type,
  label: type,
  icon: '📦',
  category: 'data',
});

/** Same default payload DesignerCanvas used for every type (including presets). */
export function createDefaultWidget({ id, type, title, scopeLevel }) {
  return {
    id,
    type,
    title,
    dataSource: REPORT_DATA_SOURCE,
    sqlQuery: '',
    scopeLevel,
    appearance: { ...DEFAULT_WIDGET_APPEARANCE },
    query: { select: [], groupBy: [], orderBy: [], limit: 100, sql: '' },
    chartConfig: { xKey: '', series: [], stacked: false },
    tableConfig: {
      columns: [],
      pageSize: 20,
      fitColumns: true,
      columnSizeMode: 'fill',
      textSize: 'sm',
      headerTextSize: 'sm',
      headerAlign: 'right',
      headerBold: true,
      headerItalic: false,
      compact: false,
    },
  };
}

function defineType({
  type,
  label,
  icon,
  category,
  editorKind,
  viewKind,
  skipDataFetch = false,
  createDefault,
  defaultLayout,
}) {
  return {
    meta: { type, label, icon, category },
    createDefault: createDefault || (({ id, scopeLevel }) =>
      createDefaultWidget({ id, type, title: label, scopeLevel })),
    Editor: null,
    View: null,
    editorKind,
    viewKind,
    skipDataFetch,
    defaultLayout: defaultLayout || { w: 6, h: 5 },
  };
}

const CORE_WIDGETS = [
  defineType({
    type: 'bar_chart',
    label: 'نمودار میله‌ای',
    icon: '📊',
    category: 'chart',
    editorKind: EDITOR_KIND.chart,
    viewKind: VIEW_KIND.chart,
  }),
  defineType({
    type: 'line_chart',
    label: 'نمودار خطی',
    icon: '📈',
    category: 'chart',
    editorKind: EDITOR_KIND.chart,
    viewKind: VIEW_KIND.chart,
  }),
  defineType({
    type: 'area_chart',
    label: 'نمودار ناحیه',
    icon: '〰️',
    category: 'chart',
    editorKind: EDITOR_KIND.chart,
    viewKind: VIEW_KIND.chart,
  }),
  defineType({
    type: 'composed_chart',
    label: 'نمودار ترکیبی',
    icon: '🔀',
    category: 'chart',
    editorKind: EDITOR_KIND.chart,
    viewKind: VIEW_KIND.chart,
  }),
  defineType({
    type: 'pie_chart',
    label: 'نمودار دایره‌ای',
    icon: '🥧',
    category: 'chart',
    editorKind: EDITOR_KIND.pie,
    viewKind: VIEW_KIND.chart,
  }),
  defineType({
    type: 'radar_chart',
    label: 'نمودار رادار',
    icon: '🕸️',
    category: 'chart',
    editorKind: EDITOR_KIND.chart,
    viewKind: VIEW_KIND.chart,
  }),
  defineType({
    type: 'radial_bar',
    label: 'گیج شعاعی',
    icon: '🔵',
    category: 'chart',
    editorKind: EDITOR_KIND.chart,
    viewKind: VIEW_KIND.chart,
  }),
  defineType({
    type: 'scatter_chart',
    label: 'نمودار پراکندگی',
    icon: '✦',
    category: 'chart',
    editorKind: EDITOR_KIND.chart,
    viewKind: VIEW_KIND.chart,
  }),
  defineType({
    type: 'table',
    label: 'جدول داده',
    icon: '📋',
    category: 'data',
    editorKind: EDITOR_KIND.table,
    viewKind: VIEW_KIND.table,
    defaultLayout: { w: 12, h: 6 },
  }),
  defineType({
    type: 'stat_card',
    label: 'کارت KPI',
    icon: '#',
    category: 'data',
    editorKind: EDITOR_KIND.stat,
    viewKind: VIEW_KIND.stat,
    defaultLayout: { w: 3, h: 4 },
    createDefault: ({ id, scopeLevel }) => ({
      ...createDefaultWidget({ id, type: 'stat_card', title: 'شاخص', scopeLevel }),
      dataSource: REPORT_DATA_SOURCE,
      appearance: {
        ...DEFAULT_WIDGET_APPEARANCE,
        themeId: 'snow',
        headerBg: '#ffffff',
        headerFg: '#0f172a',
        bodyBg: 'transparent',
        showHeader: false,
        showBorder: false,
        showPadding: false,
        fitContent: true,
      },
      statConfig: emptyKpiCard(),
    }),
  }),
  defineType({
    type: 'gauge',
    label: 'گیج هدف',
    icon: '⏲️',
    category: 'data',
    editorKind: EDITOR_KIND.gauge,
    viewKind: VIEW_KIND.gauge,
    defaultLayout: { w: 4, h: 5 },
    createDefault: ({ id, scopeLevel }) => ({
      ...createDefaultWidget({ id, type: 'gauge', title: 'گیج هدف', scopeLevel }),
      dataSource: REPORT_DATA_SOURCE,
      appearance: {
        ...DEFAULT_WIDGET_APPEARANCE,
        themeId: 'snow',
        headerBg: '#ffffff',
        headerFg: '#0f172a',
        bodyBg: '#ffffff',
      },
      gaugeConfig: {
        variant: 'arc',
        format: 'number',
        actualKey: '',
        targetKey: '',
        actualFormula: '',
        targetFormula: '',
        progressFormula: '',
        showValue: true,
        showTarget: true,
      },
    }),
  }),
  defineType({
    type: 'progress_bar',
    label: 'نوار پیشرفت',
    icon: '▬',
    category: 'data',
    editorKind: EDITOR_KIND.gauge,
    viewKind: VIEW_KIND.progress,
    defaultLayout: { w: 6, h: 3 },
    createDefault: ({ id, scopeLevel }) => ({
      ...createDefaultWidget({ id, type: 'progress_bar', title: 'پیشرفت', scopeLevel }),
      dataSource: REPORT_DATA_SOURCE,
      appearance: {
        ...DEFAULT_WIDGET_APPEARANCE,
        themeId: 'snow',
        headerBg: '#ffffff',
        headerFg: '#0f172a',
        bodyBg: '#ffffff',
        showHeader: false,
      },
      progressConfig: {
        variant: 'linear',
        format: 'percent',
        actualKey: '',
        targetKey: '',
        actualFormula: '',
        targetFormula: '',
        progressFormula: '',
        showValue: true,
        showTarget: true,
      },
    }),
  }),
];

/** Insertion order is the designer type-picker order (charts → data). */
const WIDGET_TYPES = [...CORE_WIDGETS];

export const WIDGET_REGISTRY = Object.fromEntries(
  WIDGET_TYPES.map((entry) => [entry.meta.type, entry]),
);

export const WIDGET_TYPE_META = WIDGET_TYPES.map((entry) => entry.meta);

export function getWidgetType(type) {
  return WIDGET_REGISTRY[type] || null;
}

export function widgetMeta(type) {
  return getWidgetType(type)?.meta || UNKNOWN_META(type);
}

export function shouldSkipWidgetDataFetch(type) {
  const entry = getWidgetType(type);
  if (entry) return !!entry.skipDataFetch;
  return isPresetWidgetType(type);
}

export function isChartWidgetType(type) {
  return getWidgetType(type)?.meta.category === 'chart';
}

/**
 * Editor section selector. Unknown `preset_*` still maps to preset (orphan
 * saved configs); any other unknown type returns null (general + data source).
 */
export function getWidgetEditorKind(type) {
  const entry = getWidgetType(type);
  if (entry) return entry.editorKind;
  if (isPresetWidgetType(type)) return EDITOR_KIND.preset;
  return null;
}

/**
 * View selector. Unknown `preset_*` → preset view (unsupported message).
 * Any other unknown type → chart view (previous ChartWidget fallback).
 */
export function getWidgetViewKind(type) {
  const entry = getWidgetType(type);
  if (entry) return entry.viewKind;
  if (isPresetWidgetType(type)) return VIEW_KIND.preset;
  return VIEW_KIND.chart;
}
